import { Injectable } from '@angular/core';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { map } from 'rxjs/operators';

import { throwOnGraphqlError } from '@app/core';
import { AppService } from '@app/services/app.service';
import {
  FolderGQL,
  FolderQueryVariables,
  CreateFolderGQL,
  CreateFolderMutationVariables,
  UpdateFolderGQL,
  UpdateFolderMutationVariables,
  DeleteFolderGQL,
  DeleteFolderMutationVariables,
  MoveFolderGQL,
  MoveFolderMutationVariables,
  FoldersGQL,
  FoldersQueryVariables,
  PublicFolderQueryVariables,
  PublicFolderGQL,
  MoveDocumentsToFolderMutationVariables,
  MoveDocumentsToFolderGQL,
  ShareFolderMutationVariables,
  ShareFolderGQL,
  UpdateSharingMutationVariables,
  UpdateSharingGQL,
  RemoveFolderSharingGQL,
  RemoveFolderSharingMutationVariables
} from 'src/generated/graphql.default';

@Injectable({ providedIn: 'root' })
export class FolderService {
  constructor(
    private appService: AppService,
    private foldersGQL: FoldersGQL,
    private folderGQL: FolderGQL,
    private createFolderGQL: CreateFolderGQL,
    private updateFolderGQL: UpdateFolderGQL,
    private deleteFolderGQL: DeleteFolderGQL,
    private moveFolderGQL: MoveFolderGQL,
    private publicFolderGQL: PublicFolderGQL,
    private moveDocumentsToFolderGQL: MoveDocumentsToFolderGQL,
    private shareFolderGQL: ShareFolderGQL,
    private updateSharingGQL: UpdateSharingGQL,
    private removeFolderSharingGQL: RemoveFolderSharingGQL
  ) {}

  folders(variables: FoldersQueryVariables, fetchPolicy: WatchQueryFetchPolicy = 'no-cache') {
    return this.appService.watchCacheAndNetworkBase(this.foldersGQL.watch(variables, { fetchPolicy })).pipe(
      throwOnGraphqlError(),
      map(response => response.data.folders)
    );
  }

  folder(variables: FolderQueryVariables, fetchPolicy: WatchQueryFetchPolicy = 'no-cache') {
    return this.appService.watchCacheAndNetworkBase(this.folderGQL.watch(variables, { fetchPolicy })).pipe(
      throwOnGraphqlError(),
      map(response => response.data.folder)
    );
  }

  publicFolder(variables: PublicFolderQueryVariables) {
    return this.publicFolderGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.publicFolder)
    );
  }

  create(variables: CreateFolderMutationVariables) {
    return this.createFolderGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createFolder)
    );
  }

  update(variables: UpdateFolderMutationVariables) {
    return this.updateFolderGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateFolder)
    );
  }

  delete(variables: DeleteFolderMutationVariables) {
    return this.deleteFolderGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.deleteFolder)
    );
  }

  moveDocumentsToFolder(variables: MoveDocumentsToFolderMutationVariables) {
    return this.moveDocumentsToFolderGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.moveDocumentsToFolder)
    );
  }

  removeDocumentsFromFolder(variables: Omit<MoveDocumentsToFolderMutationVariables, 'folderId'>) {
    return this.moveDocumentsToFolderGQL.mutate({ ...variables, folderId: null }).pipe(
      throwOnGraphqlError(),
      map(response => response.data.moveDocumentsToFolder)
    );
  }

  moveFolder(variables: MoveFolderMutationVariables) {
    return this.moveFolderGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.moveFolder)
    );
  }

  shareFolder(variables: ShareFolderMutationVariables) {
    return this.shareFolderGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.shareFolder)
    );
  }

  updateSharing(variables: UpdateSharingMutationVariables) {
    return this.updateSharingGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateSharing)
    );
  }

  removeFolderSharing(variables: RemoveFolderSharingMutationVariables) {
    return this.removeFolderSharingGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.removeFolderSharing)
    );
  }
}
